import { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import _ from 'lodash'
import * as colors from '../config/theme/colors'
import media, { useBreakpoint } from '../config/theme/media'

import useTranslate from 'hooks/useTranslate'

import H2 from 'components/Typography/H2'
import H3 from 'components/Typography/H3'
import P from 'components/Typography/P'
import StickyHeader from 'components/StickyHeader'
import Link from 'components/Link'
import { useDrugGroup } from '../hooks/drugsHooks'
import Button from 'components/Button'
import IdapsDrugSearch from "../containers/IdapsDrugSearch"
import Loader from 'components/Loader'
import TabbedRadio from 'components/TabbedRadio'

const Browser = styled.ol`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.gap ? `${props.gap}px` : '10px'};
  width: 100%;
  padding: 16px 0;
  margin-bottom: 0;
  margin-top: 0;
  border-bottom: 1px solid #c0c0c0;
`

const BrowserItem = styled.li`
  list-style-type: none;
`

const AlphabetButton = styled(Button)`
  background-color: ${props => props.isActive ? 'black' : '#ededed'};
  width: 40px;
  height: 40px;
  justify-content: center;
  align-items: center;
  display: flex;
  box-shadow: none;
  padding: 0;

  ${media.lessThan('md')`
    width: 32px;
    height: 32px;
  `}

  span {
    color: ${props => props.isActive ? 'white' : 'black'};
    font-size: 20px;
    font-weight: bold;
  }

  :hover, :active, :focus {
    span {color: white;}
  } 
`

const DrugsWrapper = styled.div`
  width: 100%;
  margin-top: 20px;
`

const SubGroup = styled.div`
  margin-bottom: 60px; 
  width: 100%;
`

const SubGroupResults = styled.div`
  background-color: ${colors.grey()};
  padding: 40px 30px 30px;
  position: relative;
  margin-top: 30px;
  margin-bottom: 10px;
`

const SubGroupH3 = styled(H3)`
  left: 0;
  margin: 0;
  padding: 12px 14px;
  position: absolute;
  top: -16px;
  background-color: black;
  display: inline-block;
  width: auto;
  color: white;
  font-size: 20px;
`

const DrugList = styled.ul`
  list-style: none;
  margin-left: 0;
  padding-left: 0;
`

const DrugListItem = styled.li`
  margin-bottom: 14px;
  padding: 0;
  line-height: 1.5;
  border-bottom: 1px solid ${colors.grey()};
  &:last-child {
    margin: 0;
  }
`

const BackToTopWrapper = styled.div`
  display: flex;
  justify-content: end;
`

const CountText = styled(P)`
  padding-top: 30px;
`

const LinkButton = styled(Button)`
  background-color: white;
  box-shadow: none;
  padding: 0;
  font-size: 18px;
  
  span {
    font-weight: normal;
    font-size: 18px;
    color: ${colors.hyperlink()};
    text-decoration: underline;
  }
  
  &:hover, &:active, &:focus {
    background-color: white;

    span {
      color: black;
    }
  }
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-bottom: 15px;
`

const ProductTypeWrapper = styled.div`
  margin-bottom: 30px;
`

const StyledDiv = styled.div`
  width: 100%;
`

const DrugCategories = (props) => {
  const translate = useTranslate()
  const { drugItems, parentPathName, pathKey, publicNameKey = 'name' } = props
  const [tabs, setTabs] = useState([])
  const [activeTabId, setActiveTabId] = useState('all')
  const [drugsInProductType, setDrugsInProductType] = useState(null)
  const [activeAlphabet, setActiveAlphabet] = useState()
  const [drugsGroupedByAlphabet, setDrugsGroupedByAlphabet] = useState(null)
  const browserRef = useRef(null)
  const stickyHeaderRef = useRef(null)
  const { lt } = useBreakpoint()

  useEffect(() => {
    if (drugItems && tabs.length > 0) {
      setDrugsInProductType(_.get(_.find(tabs, {id: activeTabId}), 'items'))
    }
  }, [tabs, activeTabId])

  useEffect(() => {
    if (drugsInProductType) {
      const drugsGroupedByAlphabet = useDrugGroup(drugsInProductType, 0, publicNameKey)
      setDrugsGroupedByAlphabet(drugsGroupedByAlphabet)
      setActiveAlphabet(_.head(drugsGroupedByAlphabet).alphabet)
    }
  }, [drugsInProductType])

  const handleSetTabLayout = (sections) => {
    if (sections.length > 0) {
      let tabDetails = _.map(sections, section => ({
        id: _.camelCase(section.label),
        label: section.label,
        name: section.label,
        value: _.camelCase(section.label),
        items: section.items
      }))
    
      tabDetails.unshift({
        id: 'all',
        label: translate('All'),
        name: translate('All'),
        value: 'all',
        items: drugItems
      })
      
      if (!tabs.length && tabDetails.length) {
        setTabs(tabDetails)
        setActiveTabId(tabDetails[0].id)
      }
    }
  }

  const handleTabClick = (index) => {
    if (_.isNumber(index)) {
      setActiveTabId(tabs[index].id)
      setDrugsGroupedByAlphabet(null)
    }
  }

  const handleAlphabetClick = (alphabet) => {
    setActiveAlphabet(alphabet)

    if (browserRef.current) {
      scrollToBrowser()
    }
  }

  const scrollToBrowser = () => {
    browserRef.current.scrollIntoView({ behavior: 'smooth' })
  }

  const handleSubAlphebatClick = (anchor) => {
    const id = anchor;
    const yOffset = lt('md') ? 0 : -(stickyHeaderRef.current.clientHeight);
    const element = document.getElementById(id);
    const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
    
    window.scrollTo({top: y, behavior: 'smooth'});
  }

  const renderDrugs = (drugs, isAlphabetLetter) => {
    let drugTitle = isAlphabetLetter ? `${activeAlphabet}${drugs.alphabet.toLowerCase()}` : '0-9'
    let drugList = _.get(drugs, 'record')

    return (
      <SubGroup key={drugTitle}>
        <SubGroupResults>
          <SubGroupH3 id={drugTitle}>{drugTitle}</SubGroupH3>
          <DrugList>
            {drugList.map(drug => <DrugListItem key={drug[pathKey]}><Link href={`/${parentPathName}/${encodeURIComponent(drug[pathKey])}`}>{drug[publicNameKey] || drug.name}</Link></DrugListItem>)}
          </DrugList>
        </SubGroupResults>
        <BackToTopWrapper>
          <LinkButton onClick={() => scrollToBrowser()}>{translate('BACK_TO_TOP')}</LinkButton>
        </BackToTopWrapper>
      </SubGroup>
    )
  }

  const isAlphabetLetter = activeAlphabet && activeAlphabet !== '0-9'
  const drugsInAlphabet = _.get(_.find(drugsGroupedByAlphabet, {'alphabet': activeAlphabet}), 'record')
  const resultCount = _.size(drugsInAlphabet)
  const drugsGroupedBySubAlphabets = drugsInAlphabet && useDrugGroup(drugsInAlphabet, 1, publicNameKey)

  if (_.isEmpty(drugItems)|| !parentPathName || !pathKey) {
    return <P>{translate("PRODUCT_SEARCH_NO_PRODUCTS")}</P>
  }

  return (
    <>
      {drugItems.length > 0 &&
        <>
          <H2>{translate('SITE_SEARCH_TITLE')}</H2>
          <IdapsDrugSearch
            drugs={drugItems}
            parentPathName={parentPathName}
            pathKey={pathKey}
            onSectionLoad={(sections) => handleSetTabLayout(sections)}
            publicNameKey={publicNameKey}
          />
        </>
      }
      
      {!drugsGroupedBySubAlphabets &&
        <LoadingContainer>
          <Loader />
        </LoadingContainer>
      }

      {drugsGroupedBySubAlphabets &&
        <>
          <ProductTypeWrapper role="radiogroup" aria-labelledby="filters_label">
            <H2 id="filters_label">{translate('DRUGLIST_FILTER_TITLE')}</H2>
            <p>{translate('DRUGLIST_FILTER_DESCRIPTION')}</p>
            <TabbedRadio
              options={tabs}
              activeTabId={activeTabId}
              name="category"
              handleChange={handleTabClick}
            />
          </ProductTypeWrapper>

           <StyledDiv ref={browserRef}>
            <H2>{translate('DRUGLIST_BROWSER_TITLE')}</H2>
            <Browser gap={10}>
              {_.map(drugsGroupedByAlphabet, group =>
                <BrowserItem key={group.alphabet}>
                  <AlphabetButton isActive={activeAlphabet === group.alphabet} onClick={() => handleAlphabetClick(group.alphabet)}>
                    {group.alphabet}
                  </AlphabetButton>
                </BrowserItem>
              )}
            </Browser>
          </StyledDiv>

          <CountText dangerouslySetInnerHTML={{ __html: translate('DRUGLIST_RESULT_COUNT_TEXT', { resultCount: resultCount}) }}></CountText>
         
          {isAlphabetLetter &&
            <StickyHeader disableInMobile>
              <div ref={stickyHeaderRef}>
                <Browser gap={24}>
                  {drugsGroupedBySubAlphabets.map(subAlphabet => <BrowserItem key={subAlphabet.alphabet}><LinkButton onClick={() => handleSubAlphebatClick(`${activeAlphabet}${subAlphabet.alphabet.toLowerCase()}`)}>{activeAlphabet}<b>{subAlphabet.alphabet.toLowerCase()}</b></LinkButton></BrowserItem>)}
                </Browser>
              </div>
            </StickyHeader>
          }

          <DrugsWrapper>
            {isAlphabetLetter ?
              drugsGroupedBySubAlphabets.map(subAlphabet => renderDrugs(subAlphabet, isAlphabetLetter)) :
              renderDrugs(_.find(drugsGroupedByAlphabet, {'alphabet': '0-9'}), isAlphabetLetter)}
          </DrugsWrapper>
          
        </>
      }
      
    </>
  )
}

export default DrugCategories
